import React, { useState, useEffect, useCallback } from 'react';
import {
  getKokoroHealth, getKokoroVoices, getKokoroDestinations,
  previewKokoroAudio, startKokoroGeneration, getKokoroTask, cancelKokoroTask,
  rescanKokoroAudios,
  kokoroFileUrl, tts, getLocalAIStatus, cleanTextWithFallback,
} from '../../services/kokoroService';
import { uploadFileToR2, signUrl } from '../../services/mediaSigner';

const S = {
  card: {
    background: 'rgba(10, 25, 41, 0.6)', border: '1px solid rgba(197,160,89,0.18)',
    borderRadius: '14px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px',
  },
  label: {
    fontSize: '0.7rem', fontWeight: 700, color: '#c5a059', textTransform: 'uppercase',
    letterSpacing: '0.06em', marginBottom: '4px', display: 'block',
  },
  input: {
    width: '100%', background: 'rgba(7, 21, 38, 0.8)', border: '1px solid rgba(197,160,89,0.25)',
    borderRadius: '8px', padding: '8px 12px', fontSize: '0.8rem', color: '#e2e8f0',
    outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif",
  },
  select: {
    width: '100%', background: 'rgba(7, 21, 38, 0.8)', border: '1px solid rgba(197,160,89,0.25)',
    borderRadius: '8px', padding: '8px 10px', fontSize: '0.8rem', color: '#fff', outline: 'none',
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #c5a059 0%, #8a6a2e 100%)', color: '#fff', border: 'none',
    borderRadius: '10px', padding: '9px 18px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '7px', whiteSpace: 'nowrap',
  },
  btnSecondary: {
    background: 'rgba(197,160,89,0.1)', color: '#c5a059', border: '1px solid rgba(197,160,89,0.3)',
    borderRadius: '10px', padding: '8px 14px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '7px', whiteSpace: 'nowrap',
  },
  btnDanger: {
    background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '10px', padding: '8px 12px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
  },
  badge: (color) => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '20px',
    fontSize: '0.65rem', fontWeight: 700, background: `${color}22`, color, border: `1px solid ${color}44`,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  }),
};

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_{2,}/g, '_')
    .slice(0, 90) || 'audio_kokoro';

const AdminEstudioVoz = () => {
  const [health, setHealth] = useState(null);
  const [voices, setVoices] = useState([]);
  const [destinationInfo, setDestinationInfo] = useState(null);

  const SETTINGS_KEY = 'vortex.kokoro.settings';
  const readSaved = () => {
    try {
      return JSON.parse(typeof localStorage !== 'undefined' ? localStorage.getItem(SETTINGS_KEY) : null) || {};
    } catch (_) {
      return {};
    }
  };
  const saved = React.useRef(readSaved()).current;

  const [voice, setVoice] = useState(saved.voice || 'ef_dora');
  const [speed, setSpeed] = useState(typeof saved.speed === 'number' ? saved.speed : 1.0);
  const [format, setFormat] = useState(saved.format === 'wav' || saved.format === 'mp3' ? saved.format : 'mp3');
  const [destination, setDestination] = useState(saved.destination || '');
  const [filename, setFilename] = useState(saved.filename || '');
  const [text, setText] = useState('');

  const [status, setStatus] = useState('');
  const [statusKind, setStatusKind] = useState('info'); // info | ok | error
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState('');
  const [busy, setBusy] = useState(''); // '' | 'preview' | 'generate' | 'load' | 'r2upload'
  const [lastResult, setLastResult] = useState(null);
  const [lmStatus, setLmStatus] = useState({ available: false, models: [], chatModel: '' });
  const [lmModel, setLmModel] = useState('');
  const [lmConcurrency, setLmConcurrency] = useState(3);
  const [genProgress, setGenProgress] = useState({ taskId: '', percent: 0, phase: '', message: '', status: '', stalled: false });
  const fileRef = React.useRef(null);
  const [r2File, setR2File] = useState(null);
  const [r2Upload, setR2Upload] = useState({ uploading: false, percent: 0, url: '', key: '', error: '' });
  const r2FileRef = React.useRef(null);
  const previewUrlRef = React.useRef(null);
  const origTextRef = React.useRef(null);
  const voiceAudioRef = React.useRef(null);
  const voiceSampleRef = React.useRef(null);
  const [voiceSample, setVoiceSample] = useState({ playing: '', url: null, name: '' });
  const [pitch, setPitch] = useState(typeof saved.pitch === 'number' ? saved.pitch : 0);
  const [volumeDb, setVolumeDb] = useState(typeof saved.volumeDb === 'number' ? saved.volumeDb : 0);
  const [bassDb, setBassDb] = useState(typeof saved.bassDb === 'number' ? saved.bassDb : 0);
  const [trebleDb, setTrebleDb] = useState(typeof saved.trebleDb === 'number' ? saved.trebleDb : 0);
  const [cadence, setCadence] = useState(saved.cadence || 'auto');
  const [quality, setQuality] = useState(['low', 'normal', 'hd'].includes(saved.quality) ? saved.quality : 'normal');
  previewUrlRef.current = previewUrl;

  const loadBasics = useCallback(async () => {
    try {
      const [, v, d] = await Promise.all([
        getKokoroHealth().then((h) => setHealth(h)),
        getKokoroVoices().then((r) => setVoices(r.voices || [])),
        getKokoroDestinations().then((r) => setDestinationInfo(r)),
      ]);
      if (!d) return;
      setDestination((prev) => prev || d.default || '');
    } catch (e) {
      setStatus(`Servidor Kokoro no disponible: ${e.message}`);
      setStatusKind('error');
      setHealth(null);
    }
  }, []);

  useEffect(() => {
    loadBasics();
    getLocalAIStatus().then(setLmStatus).catch(() => setLmStatus({ available: false, models: [] }));
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      if (voiceSampleRef.current) URL.revokeObjectURL(voiceSampleRef.current);
    };
  }, [loadBasics]);

  // Persiste la personalización de la voz (y formato/destino/nombre) en
  // localStorage para que se conserve al volver a entrar a la pantalla.
  useEffect(() => {
    try {
      localStorage.setItem(
        SETTINGS_KEY,
        JSON.stringify({
          voice, speed, format, destination, filename,
          pitch, volumeDb, bassDb, trebleDb, cadence, quality,
        })
      );
    } catch (_) {
      /* almacenamiento no disponible */
    }
  }, [voice, speed, format, destination, filename, pitch, volumeDb, bassDb, trebleDb, cadence, quality]);

  // Vigila el estado real del servidor Kokoro cada pocos segundos para que la
  // insignia ("Servidor activo") no se quede en verde cuando el proceso cae.
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      try {
        const h = await getKokoroHealth();
        if (!stopped) setHealth(h);
      } catch (_) {
        if (!stopped) setHealth(null);
      }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (!lmModel && lmStatus.chatModel) setLmModel(lmStatus.chatModel);
  }, [lmModel, lmStatus.chatModel]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatus('Leyendo documento…');
    setBusy('load');
    try {
      const content = await tts.fileToText(file);
      setText(content);
      if (!filename) setFilename(slugify(file.name.replace(/\.[^.]+$/, '')));
      origTextRef.current = null;
      setStatus(`✅ Texto extraído (${content.length.toLocaleString()} caracteres).`);
      setStatusKind('ok');
    } catch (err) {
      setStatus(`❌ No se pudo leer el archivo: ${err.message}`);
      setStatusKind('error');
    } finally {
      setBusy('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

const playVoiceSample = async (vid, name) => {
    try {
      if (voiceSampleRef.current) {
        URL.revokeObjectURL(voiceSampleRef.current);
        voiceSampleRef.current = null;
      }
      setVoiceSample({ playing: vid, url: null, name });
      setStatus(`🔊 Generando muestra de la voz ${name || vid}…`);
      setStatusKind('info');
      const blob = await previewKokoroAudio({
        text: `Hola, esta es una muestra de la voz ${vid} en español. Mi nombre es ${name || vid}.`,
        voice: vid,
        speed: 1.0,
        format: 'wav',
      });
      const url = URL.createObjectURL(blob);
      voiceSampleRef.current = url;
      setVoiceSample({ playing: vid, url, name });
      const el = voiceAudioRef.current;
      if (el) {
        el.src = url;
        el.play().catch(() => {});
      }
      setStatus(`🔊 Muestra de la voz ${name || vid}. Púlsala para escucharla de nuevo.`);
      setStatusKind('ok');
    } catch (err) {
      setVoiceSample((s) => ({ ...s, playing: '' }));
      setStatus(`❌ No se pudo generar la muestra de ${vid}: ${err.message}`);
      setStatusKind('error');
    }
  };

const handlePreview = async () => {
    if (!text.trim()) {
      setStatus('⚠️ Escribe o pega un texto para escuchar el preview.');
      setStatusKind('error');
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setBusy('preview');
    setStatus('Generando preview…');
    setStatusKind('info');
    try {
      const blob = await previewKokoroAudio({ text: text.slice(0, 1200), voice, speed, format: 'wav', ...personalization() });
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewName(`preview_${voice}_${speed}x.wav`);
      setStatus('✅ Preview listo. Escúchalo antes de guardar.');
      setStatusKind('ok');
    } catch (err) {
      setStatus(`❌ Error en preview: ${err.message}`);
      setStatusKind('error');
    } finally {
      setBusy('');
    }
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const pollTask = async (taskId) => {
    setGenProgress((p) => ({ ...p, taskId }));
    let finished = false;
    while (!finished) {
      let t;
      try {
        t = await getKokoroTask(taskId);
      } catch (err) {
        throw new Error(`El proceso se cortó (sin respuesta del servidor): ${err.message}`);
      }
      const running = t.status === 'running' || t.status === 'pending';
      const silence = (Date.now() / 1000) - (t.updated_at || 0);
      setGenProgress({
        taskId,
        percent: t.progress || 0,
        phase: t.phase || '',
        message: t.message || '',
        status: t.status,
        stalled: running && silence > 45,
      });
      if (running && silence > 180) {
        throw new Error('El proceso se detuvo (sin avance en más de 3 minutos). Reintenta o revisa el log del servidor.');
      }
      if (t.status === 'done') {
        setLastResult(t.result);
        setStatus(`✅ Audio guardado: ${t.result?.filename} (${((t.result?.size || 0) / 1024).toFixed(1)} KB, ${t.result?.seconds}s).`);
        setStatusKind('ok');
        finished = true;
      } else if (t.status === 'cancelled') {
        setStatus('⏹️ Generación cancelada.');
        setStatusKind('info');
        finished = true;
      } else if (t.status === 'error') {
        throw new Error(t.error || 'Error en la generación.');
      } else {
        await sleep(900);
      }
    }
  };

  const handleGenerate = async () => {
    if (!text.trim()) {
      setStatus('⚠️ Escribe o pega un texto para generar el audio.');
      setStatusKind('error');
      return;
    }
    if (!destination.trim()) {
      setStatus('⚠️ Indica la carpeta destino.');
      setStatusKind('error');
      return;
    }
    setBusy('generate');
    setLastResult(null);
    setGenProgress({ taskId: '', percent: 0, phase: 'iniciando…', message: '', status: 'starting', stalled: false });
    try {
      const { task_id } = await startKokoroGeneration({
        text,
        voice,
        speed,
        format,
        destination: destination.trim(),
        filename: filename || slugify(text.split(/[\n.]/)[0]) || 'audio_kokoro',
        ...personalization(),
      });
      await pollTask(task_id);
    } catch (err) {
      setGenProgress((p) => ({ ...p, status: 'error' }));
      setStatus(`❌ ${err.message}`);
      setStatusKind('error');
    } finally {
      setBusy('');
    }
  };

  const handleCancelGeneration = async () => {
    if (!genProgress.taskId) return;
    try {
      await cancelKokoroTask(genProgress.taskId);
      setStatus('⏹️ Cancelando… se detendrá al terminar el tramo actual.');
      setStatusKind('info');
    } catch (err) {
      setStatus(`❌ No se pudo cancelar: ${err.message}`);
      setStatusKind('error');
    }
  };

  const handleRescan = async () => {
    setBusy('rescan');
    setStatus('Regenerando biblioteca de audios (localAudios.json)…');
    setStatusKind('info');
    try {
      const r = await rescanKokoroAudios();
      setStatus(`✅ Bibliotecas actualizadas: ${r.count} audios indexados.`);
      setStatusKind('ok');
    } catch (err) {
      setStatus(`❌ Error en rescan: ${err.message}`);
      setStatusKind('error');
    } finally {
      setBusy('');
    }
  };

  const handleR2FileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) {
      setStatus('Selecciona un archivo .zip válido.');
      setStatusKind('error');
      e.target.value = '';
      return;
    }
    setR2File(file);
    setR2Upload((p) => ({ ...p, percent: 0, url: '', key: '', error: '' }));
    setStatus(`ZIP listo: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)`);
    setStatusKind('info');
  };

  const handleR2Upload = async () => {
    if (!r2File) return;
    setBusy('r2upload');
    setR2Upload({ uploading: true, percent: 0, url: '', key: '', error: '' });
    setStatus('Subiendo ZIP a Cloudflare R2 (multipart)…');
    setStatusKind('info');
    try {
      const result = await uploadFileToR2(r2File, {
        onProgress: ({ percent }) => setR2Upload((p) => ({ ...p, percent: Math.round(percent) })),
      });
      const signed = await signUrl(result.publicUrl);
      setR2Upload({ uploading: false, percent: 100, url: signed, key: result.key, error: '' });
      setStatus(`✅ ZIP subido a reels/AUDIOS: ${result.key}`);
      setStatusKind('ok');
    } catch (err) {
      setR2Upload((p) => ({ ...p, uploading: false, error: err.message }));
      setStatus(`❌ Error subiendo ZIP: ${err.message}`);
      setStatusKind('error');
    } finally {
      setBusy('');
      if (r2FileRef.current) r2FileRef.current.value = '';
      setR2File(null);
    }
  };

  const startDevServer = async () => {
    setBusy('devstart');
    setStatus('Arrancando el servidor de Kokoro desde el dev server…');
    setStatusKind('info');
    try {
      const res = await fetch('/__kokoro/start', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      setStatus(`${data.message} Esperando a que levante…`);
      for (let i = 0; i < 80; i++) {
        await new Promise((r) => setTimeout(r, 500));
        try {
          const h = await getKokoroHealth();
          if (h?.ok) {
            await loadBasics();
            setStatus('✅ Servidor de Kokoro en línea.');
            setStatusKind('ok');
            return;
          }
        } catch (_) { /* el servidor aún no responde */ }
      }
      throw new Error('El servidor tardó demasiado en responder. Revisa backend_kokoro/kokoro_dev.log');
    } catch (err) {
      setStatus(`❌ No se pudo arrancar el servidor: ${err.message}`);
      setStatusKind('error');
    } finally {
      setBusy('');
    }
  };

  const refreshLm = async () => {
    setLmStatus(await getLocalAIStatus());
  };

  const handleCleanText = async () => {
    if (!text.trim()) {
      setStatus('⚠️ Escribe o pega un texto para limpiarlo con la IA.');
      setStatusKind('error');
      return;
    }
    setBusy('clean');
    setStatus('✨ Consultando modelo local…');
    setStatusKind('info');
    try {
      const cleaned = await cleanTextWithFallback(text.trim(), {
        modelId: lmModel || undefined,
        concurrency: lmConcurrency,
        onProgress: (done, total) =>
          setStatus(`✨ Limpiando con Gemini… chunk ${done}/${total}${lmModel || lmStatus.chatModel ? ` (fallback: ${lmModel || lmStatus.chatModel})` : ''}`),
      });
      if (!cleaned) throw new Error('El modelo devolvió texto vacío.');
      origTextRef.current = text;
      setText(cleaned);
      setStatus(`✅ Texto limpio y formateado para lectura (${cleaned.length.toLocaleString()} caracteres). Previsualiza antes de guardar.`);
      setStatusKind('ok');
    } catch (err) {
      setStatus(`❌ No se pudo limpiar el texto: ${err.message}`);
      setStatusKind('error');
    } finally {
      setBusy('');
    }
  };

  const handleRevertClean = () => {
    if (origTextRef.current) {
      setText(origTextRef.current);
      origTextRef.current = null;
      setStatus('↩️ Se restauró el texto original.');
      setStatusKind('info');
    }
  };

  const serverOk = !!health?.ok;

  const personalization = () => ({ pitch, volume_db: volumeDb, bass_db: bassDb, treble_db: trebleDb, cadence, quality });

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Encabezado */}
      <div style={{ ...S.card, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontSize: '1.4rem' }}>🎙️</span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h2 style={{ margin: 0, fontSize: '1rem', color: '#fff', fontWeight: 700 }}>Estudio de Voz (Kokoro · Español)</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: '#94a3b8' }}>
            Genera audios con las voces de Kokoro (ef_dora, em_alex, em_santa), con preview y guardado en la carpeta que elijas.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {serverOk ? (
            <span style={S.badge('#34d399')}>Servidor activo</span>
          ) : (
            <span style={S.badge('#f87171')}>Sin servidor</span>
          )}
          {health && (
            <span style={S.badge('#c5a059')}>
              {health.cuda ? 'GPU' : 'CPU'} · {health.sample_rate} Hz{health.ffmpeg ? ' · ffmpeg ✓' : ''}
            </span>
          )}
          <button onClick={loadBasics} className="adm-btn adm-btn--secondary">Reconectar</button>
        </div>
      </div>

      {/* Estado del servidor / error */}
      {!serverOk && (
        <div style={{ ...S.card, marginTop: '12px', borderColor: 'rgba(248,113,113,0.35)' }}>
          <p style={{ margin: 0, fontSize: '0.78rem', color: '#f87171', lineHeight: 1.6 }}>
            No se pudo conectar con el servidor local de Kokoro.
            <br />Inícialo con <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 5px', borderRadius: 4 }}>
            run_server.bat</code> en <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 5px', borderRadius: 4 }}>backend_kokoro/</code>{' '}
            (Python del env de Kokoro, puerto 8500).
          </p>
          {import.meta.env.DEV && (
            <button
              onClick={startDevServer}
              disabled={!!busy}
              className="adm-btn adm-btn--primary"
              style={{ alignSelf: 'flex-start' }}
            >
              🚀 {busy === 'devstart' ? 'Arrancando…' : 'Arrancar servidor Kokoro (1 clic)'}
            </button>
          )}
        </div>
      )}

      <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
        {/* Bloque de texto */}
        <div style={S.card}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
            <span style={{ ...S.label, margin: 0 }}>Texto a convertir</span>
            <label className="adm-btn adm-btn--secondary" style={{ cursor: 'pointer', display: 'inline-flex' }}>
              📁 {busy === 'load' ? 'Leyendo…' : 'Subir .txt / .pdf / .docx'}
              <input ref={fileRef} type="file" accept=".txt,.md,.pdf,.doc,.docx" onChange={handleFile} style={{ display: 'none' }} />
            </label>
            <button
              onClick={handleCleanText}
              disabled={!!busy || !text.trim()}
              className="adm-btn adm-btn--primary"
            >
              ✨ {busy === 'clean' ? 'Limpiando… (Gemini)…' : 'Limpiar con IA (Gemini → local)'}
            </button>
            {origTextRef.current && text !== origTextRef.current && (
              <button onClick={handleRevertClean} className="adm-btn adm-btn--secondary">
                ↩️ Deshacer IA
              </button>
            )}
            <button onClick={refreshLm} className="adm-btn adm-btn--secondary" title="Comprobar LM Studio">
              🤖 {lmStatus.available ? `${lmStatus.models.length} modelos` : 'IA local: apagada'}
            </button>
            {lmStatus.available && lmStatus.models.length > 1 && (
              <select
                value={lmModel}
                onChange={(e) => setLmModel(e.target.value)}
                style={{ ...S.select, width: 'auto', padding: '6px 8px', fontSize: '0.7rem' }}
                title="Modelo local para limpiar el texto"
              >
                {lmStatus.models.map((m) => (
                  <option key={m} value={m}>
                    {m.replace(/^.*\//, '')}{m === lmStatus.chatModel ? ' ★' : ''}
                  </option>
                ))}
              </select>
            )}
            <select
              value={lmConcurrency}
              onChange={(e) => setLmConcurrency(Number(e.target.value))}
              style={{ ...S.select, width: 'auto', padding: '6px 8px', fontSize: '0.7rem' }}
              title="Chunks en paralelo al limpiar (más = más rápido, pero satura LM Studio)"
            >
              {[1, 2, 3, 4, 5, 6].map((c) => (
                <option key={c} value={c}>
                  ⚡ {c} en paralelo
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                setText('');
                setPreviewUrl(null);
                setLastResult(null);
                origTextRef.current = null;
              }}
              className="adm-btn adm-btn--danger"
            >
              🗑️ Limpiar
            </button>
            <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#64748b' }}>
              {text.length.toLocaleString()} caracteres
            </span>
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'Pega aquí el texto (o sube un .txt / .pdf / .docx):\n\nArtículo 1. La Secretaría de Marina...'}
            style={{ ...S.input, resize: 'vertical', minHeight: '300px', lineHeight: 1.65, fontSize: '0.85rem' }}
          />
          {lmStatus.available ? (
            <p style={{ margin: 0, fontSize: '0.68rem', color: '#64748b' }}>
              ✨ La limpieza usa <b style={{ color: '#34d399' }}>Gemini</b> primero; si no está disponible, cae al modelo
              local (<b style={{ color: '#c5a059' }}>{lmStatus.chatModel || lmStatus.models[0]}</b>). Corrige ortografía,
              quita símbolos raros y formatea para el lector (pausas y cadencia). Se procesa en chunks paralelos
              ({lmConcurrency} a la vez) para textos largos.
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: '0.68rem', color: '#64748b' }}>
              ✨ La limpieza usa <b style={{ color: '#34d399' }}>Gemini</b> (no requiere LM Studio). Si Gemini no responde,
              abre <b style={{ color: '#c5a059' }}>LM Studio</b>, carga un modelo y activa su servidor local (puerto 1234)
              como respaldo.
            </p>
          )}
          {lastResult?.public_url && (
            <div style={{ fontSize: '0.72rem', color: '#34d399' }}>
              🌐 URL pública generada: <code>{lastResult.public_url}</code>
            </div>
          )}
        </div>

        {/* Subida ZIP a R2 (multipart) */}
        <div style={{ ...S.card, marginTop: '12px', borderColor: 'rgba(197,160,89,0.35)' }}>
          <span style={S.label}>Subir ZIP a Cloudflare R2</span>
          <p style={{ margin: 0, fontSize: '0.68rem', color: '#94a3b8' }}>
            Bucket <code>reels</code> · carpeta <code>AUDIOS</code> · usa multipart para archivos mayores de 300 MB.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginTop: '10px' }}>
            <label className="adm-btn adm-btn--secondary" style={{ cursor: 'pointer', display: 'inline-flex' }}>
              📦 {r2File ? 'Cambiar ZIP' : 'Seleccionar .zip'}
              <input
                ref={r2FileRef}
                type="file"
                accept=".zip,application/zip"
                onChange={handleR2FileChange}
                style={{ display: 'none' }}
              />
            </label>
            {r2File && (
              <span style={{ fontSize: '0.7rem', color: '#e2e8f0' }}>
                {r2File.name} · {(r2File.size / 1024 / 1024).toFixed(1)} MB
              </span>
            )}
            <button
              onClick={handleR2Upload}
              disabled={!!busy || !r2File || r2Upload.uploading}
              className="adm-btn adm-btn--primary"
              style={{ marginLeft: 'auto' }}
            >
              🚀 {busy === 'r2upload' ? 'Subiendo…' : 'Subir ZIP a R2'}
            </button>
          </div>
          {r2Upload.percent > 0 && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#cbd5e1' }}>
                <span>Progreso</span>
                <span style={{ fontWeight: 800, color: '#c5a059' }}>{r2Upload.percent}%</span>
              </div>
              <div style={{ height: '12px', background: 'rgba(255,255,255,0.06)', borderRadius: '9px', overflow: 'hidden', marginTop: '6px' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(100, Math.max(0, r2Upload.percent))}%`,
                    background: 'linear-gradient(135deg,#c5a059,#8a6a2e)',
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>
            </div>
          )}
          {r2Upload.error && (
            <div style={{ marginTop: '8px', fontSize: '0.7rem', color: '#f87171' }}>❌ {r2Upload.error}</div>
          )}
          {r2Upload.url && (
            <div style={{ marginTop: '10px', fontSize: '0.72rem', color: '#34d399', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span>📄 <strong>{r2Upload.key}</strong></span>
              <a href={r2Upload.url} target="_blank" rel="noreferrer" className="adm-btn adm-btn--secondary" style={{ alignSelf: 'flex-start', textDecoration: 'none' }}>
                ▶️ Reproducir / descargar desde R2
              </a>
            </div>
          )}
        </div>

        {/* Configuración de generación (bajo el texto, en un contenedor) */}
        <div style={{
          background: 'rgba(10, 25, 41, 0.35)',
          border: '1px solid rgba(197,160,89,0.18)',
          borderRadius: '14px',
          padding: '16px 18px',
        }}>
          <span style={S.label}>Configuración de generación</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginTop: '14px' }}>
          <div style={S.card}>
            <span style={S.label}>Voz (español)</span>
            <select value={voice} onChange={(e) => setVoice(e.target.value)} style={S.select}>
              {voices.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.gender === 'f' ? '♀️' : '♂️'} {v.id} — {v.name}
                  {v.downloaded ? '' : ' (por descargar)'}
                </option>
              ))}
              {voices.length === 0 && <option value="ef_dora">ef_dora — Dora (femenina)</option>}
            </select>
            <p style={{ margin: 0, fontSize: '0.66rem', color: '#64748b' }}>
              {voices.find((v) => v.id === voice)?.description || 'Voz femenina clara y natural.'}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {voices.map((v) => (
                <button
                  key={v.id}
                  onClick={() => playVoiceSample(v.id, v.name)}
                  disabled={voiceSample.playing === v.id && !voiceSample.url}
                  className="adm-btn adm-btn--secondary"
                  style={{ fontSize: '0.68rem', padding: '5px 10px' }}
                >
                  {voiceSample.playing === v.id && !voiceSample.url ? 'Generando…' : `▶ ${v.id}`}
                </button>
              ))}
            </div>
            {voiceSample.url && (
              <div style={{ marginTop: '8px' }}>
                <audio
                  ref={voiceAudioRef}
                  controls
                  src={voiceSample.url}
                  style={{ width: '100%', height: '32px' }}
                />
              </div>
            )}
          </div>

          <div style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ ...S.label, margin: 0 }}>Velocidad (speed)</span>
              <span style={{ fontSize: '0.75rem', color: '#c5a059', fontWeight: 700 }}>{speed.toFixed(2)}x</span>
            </div>
            <input
              type="range" min="0.5" max="2" step="0.05"
              value={speed}
              onChange={(e) => setSpeed(parseFloat(e.target.value))}
              style={{ width: '100%', accentColor: '#c5a059' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#64748b' }}>
              <span>0.5x Lento</span><span>1.0x Normal</span><span>2.0x Rápido</span>
            </div>
          </div>

          <div style={S.card}>
            <span style={S.label}>Formato de audio</span>
            <div style={{ display: 'flex', gap: '10px' }}>
              {['wav', 'mp3'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFormat(f)}
                  style={{
                    flex: 1, padding: '8px', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem',
                    background: format === f ? 'linear-gradient(135deg, #c5a059 0%, #8a6a2e 100%)' : 'rgba(255,255,255,0.04)',
                    color: format === f ? '#fff' : '#94a3b8',
                    border: format === f ? 'none' : '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  {f === 'wav' ? 'WAV (nativo)' : 'MP3 (ffmpeg)'}
                </button>
              ))}
            </div>
            {format === 'mp3' && health && !health.ffmpeg && (
              <p style={{ margin: 0, fontSize: '0.66rem', color: '#f87171' }}>
                ffmpeg no detectado: usa WAV o instala ffmpeg para MP3.
              </p>
            )}
            <div style={{ marginTop: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ ...S.label, margin: 0 }}>Calidad (tamaño del archivo)</span>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                {[
                  { k: 'low', label: 'Baja', hint: '64 kbps · ~0.5 MB/min' },
                  { k: 'normal', label: 'Normal', hint: '128 kbps · ~1 MB/min' },
                  { k: 'hd', label: 'HD', hint: '256 kbps · ~1.9 MB/min' },
                ].map((c) => (
                  <button
                    key={c.k}
                    title={c.hint}
                    onClick={() => setQuality(c.k)}
                    style={{
                      flex: 1, padding: '8px', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '0.72rem',
                      background: quality === c.k ? 'linear-gradient(135deg, #b45309 0%, #c5a059 100%)' : 'rgba(255,255,255,0.04)',
                      color: quality === c.k ? '#fff' : '#94a3b8',
                      border: quality === c.k ? 'none' : '1px solid rgba(255,255,255,0.12)',
                    }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <p style={{ margin: '6px 0 0', fontSize: '0.62rem', color: '#64748b' }}>
                Baja recorta el tamaño final del audio; HD la maximiza. En WAV, Baja remuestrea a 16 kHz.
              </p>
            </div>
          </div>

          <div style={S.card}>
            <span style={S.label}>Personalización de la voz</span>
            <p style={{ margin: 0, fontSize: '0.66rem', color: '#94a3b8', lineHeight: 1.5 }}>
              Ajusta tono, volumen y ecualización (post-proceso con ffmpeg) y la cadencia (nativa de Kokoro). Pruébalo con el preview.
            </p>
            {[
              { k: 'pitch', label: 'Tono (pitch)', v: pitch, set: setPitch, min: -12, max: 12, step: 1, fmt: (n) => `${n > 0 ? '+' : ''}${n} st` },
              { k: 'volumeDb', label: 'Volumen (gain)', v: volumeDb, set: setVolumeDb, min: -20, max: 20, step: 1, fmt: (n) => `${n > 0 ? '+' : ''}${n} dB` },
              { k: 'bassDb', label: 'Graves (bass)', v: bassDb, set: setBassDb, min: -12, max: 12, step: 1, fmt: (n) => `${n > 0 ? '+' : ''}${n} dB` },
              { k: 'trebleDb', label: 'Agudos (treble)', v: trebleDb, set: setTrebleDb, min: -12, max: 12, step: 1, fmt: (n) => `${n > 0 ? '+' : ''}${n} dB` },
            ].map((c) => (
              <div key={c.k} style={{ marginTop: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.68rem', color: '#cbd5e1', fontWeight: 700 }}>{c.label}</span>
                  <span style={{ fontSize: '0.7rem', color: '#c5a059', fontWeight: 700 }}>{c.fmt(c.v)}</span>
                </div>
                <input
                  type="range" min={c.min} max={c.max} step={c.step} value={c.v}
                  onChange={(e) => c.set(parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: '#c5a059' }}
                />
              </div>
            ))}
            <div style={{ marginTop: '10px' }}>
              <span style={{ ...S.label }}>Cadencia (pausas)</span>
              <select value={cadence} onChange={(e) => setCadence(e.target.value)} style={S.select}>
                <option value="auto">Auto (por párrafos de Kokoro)</option>
                <option value="sentences">Marcada · más pausas entre frases</option>
                <option value="paragraphs">Fluida · pausas largas</option>
              </select>
            </div>
          </div>

          <div style={S.card}>
            <span style={S.label}>Carpeta destino (ruta absoluta)</span>
            <input
              type="text" value={destination}
              onChange={(e) => setDestination(e.target.value)}
              style={S.input}
              placeholder={destinationInfo?.default || 'C:\\Users\\...\\pagina_ascensos_app_vortex\\public\\audios\\NUEVA_CARPETA'}
            />
            {destinationInfo?.folders?.length > 0 && (
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    setDestination(`${destinationInfo.base_dir}\\${e.target.value.split('/').join('\\')}`);
                  }
                }}
                style={S.select}
                defaultValue=""
              >
                <option value="" disabled>Seleccionar carpeta existente…</option>
                {destinationInfo.folders.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            )}
            <p style={{ margin: 0, fontSize: '0.66rem', color: '#64748b' }}>
              Si eliges una carpeta bajo <code>public/audios</code>, el audio se publica al instante en la web.
            </p>
          </div>

          <div style={S.card}>
            <span style={S.label}>Nombre del archivo</span>
            <input
              type="text" value={filename}
              onChange={(e) => setFilename(e.target.value)}
              style={S.input}
              placeholder="(vacío = se genera del primer renglón)"
            />
          </div>

          {/* Acciones */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button onClick={handlePreview} disabled={!!busy} className="adm-btn adm-btn--primary" style={{ width: '100%', justifyContent: 'center' }}>
              ▶️ {busy === 'preview' ? 'Generando preview…' : 'Escuchar preview'}
            </button>
            <button onClick={handleGenerate} disabled={!!busy} className="adm-btn adm-btn--primary" style={{ width: '100%', justifyContent: 'center', background: 'linear-gradient(135deg, #b45309 0%, #c5a059 100%)' }}>
              💾 {busy === 'generate' ? 'Generando…' : 'Generar y guardar audio'}
            </button>
            {serverOk && (
              <button onClick={handleRescan} disabled={!!busy} className="adm-btn adm-btn--secondary" style={{ width: '100%', justifyContent: 'center' }}>
                🔄 Actualizar biblioteca (localAudios.json)
              </button>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Barra de progreso de la conversión */}
      {genProgress.taskId && genProgress.status !== 'done' && genProgress.status !== 'cancelled' && (
        <div style={{ ...S.card, marginTop: '16px', borderColor: 'rgba(197,160,89,0.35)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
            <span style={S.label}>Conversión a audio</span>
            <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#c5a059' }}>
              {genProgress.status === 'error' ? 'ERROR' : `${genProgress.percent}%`}
            </span>
          </div>
          <div style={{ height: '14px', background: 'rgba(255,255,255,0.06)', borderRadius: '9px', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%', width: `${Math.min(100, Math.max(0, genProgress.percent))}%`,
                background: genProgress.status === 'error'
                  ? 'linear-gradient(135deg,#ef4444,#f97316)'
                  : 'linear-gradient(135deg,#c5a059,#8a6a2e)',
                transition: 'width 0.6s ease',
              }}
            />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
              {genProgress.phase || 'preparando…'}
              {genProgress.message ? ` · ${genProgress.message}` : ''}
            </span>
            {['starting', 'pending', 'running', 'cancelling'].includes(genProgress.status) && (
              <button onClick={handleCancelGeneration} className="adm-btn adm-btn--danger">
                ⏹️ Cancelar
              </button>
            )}
          </div>
          {genProgress.stalled && (
            <span style={{ fontSize: '0.7rem', color: '#fbbf24' }}>
              ⚠️ Sin avance en más de 45s (señal de proceso detenido o muy lento en CPU). Se reportará si no retoma.
            </span>
          )}
        </div>
      )}

      {/* Preview / resultado */}
      {(previewUrl || lastResult) && (
        <div style={{ ...S.card, marginTop: '16px' }}>
          <span style={S.label}>Preview / resultado</span>
          {previewUrl && (
            <audio controls src={previewUrl} style={{ width: '100%' }} />
          )}
          {previewUrl && (
            <a href={previewUrl} download={previewName} className="adm-btn adm-btn--secondary" style={{ alignSelf: 'flex-start', textDecoration: 'none' }}>
              ⬇️ Descargar preview
            </a>
          )}
          {lastResult && (
            <div style={{ fontSize: '0.78rem', color: '#e2e8f0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span>📄 <strong>{lastResult.filename}</strong></span>
              <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                {lastResult.path} · {(lastResult.size / 1024).toFixed(1)} KB · {lastResult.seconds}s · {lastResult.format}
              </span>
              <a
                href={kokoroFileUrl(lastResult.path)}
                target="_blank" rel="noreferrer"
                className="adm-btn adm-btn--secondary"
                style={{ alignSelf: 'flex-start', textDecoration: 'none' }}
              >
                ▶️ Reproducir el archivo guardado
              </a>
            </div>
          )}
        </div>
      )}

      {/* Mensaje de estado */}
      {status && (
        <div
          style={{
            marginTop: '14px', padding: '12px 14px', borderRadius: '10px',
            fontSize: '0.78rem', lineHeight: 1.5,
            background: statusKind === 'error' ? 'rgba(239,68,68,0.12)' : statusKind === 'ok' ? 'rgba(52,211,153,0.12)' : 'rgba(197,160,89,0.12)',
            border: `1px solid ${statusKind === 'error' ? 'rgba(248,113,113,0.35)' : statusKind === 'ok' ? 'rgba(52,211,153,0.35)' : 'rgba(197,160,89,0.35)'}`,
            color: statusKind === 'error' ? '#f87171' : statusKind === 'ok' ? '#6ee7b7' : '#d8b978',
          }}
        >
          {status}
        </div>
      )}
    </div>
  );
};

export default AdminEstudioVoz;